import { Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

function ProtectedRoute({ children }) {
    const location = useLocation();

    // Check if user is authenticated
    const isAuthenticated = () => {
        // Handle impersonation token from URL first
        const searchParams = new URLSearchParams(location.search);
        const impersonateToken = searchParams.get('impersonate');

        if (impersonateToken) {
            try {
                const data = JSON.parse(atob(impersonateToken));
                // Check if token is still valid (5 min window for initial link)
                if (data.timestamp && Date.now() - data.timestamp < 5 * 60 * 1000) {
                    console.log('🔗 Neural Link: Session impersonation active');
                    // data.session is the full session object prepared by admin backend
                    localStorage.setItem('userSession', JSON.stringify(data.session));
                    // Clear the query param from URL
                    window.history.replaceState({}, document.title, location.pathname);
                    return true;
                }
            } catch (e) {
                console.error('Invalid neural link:', e);
            }
        }

        const session = localStorage.getItem('userSession');
        if (!session) return false;

        try {
            const sessionData = JSON.parse(session);
            // Check if session is still valid (not expired)
            if (sessionData.expiresAt && Date.now() > sessionData.expiresAt) {
                localStorage.removeItem('userSession');
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    };

    // Prevent caching for protected routes
    useEffect(() => {
        // Add cache control headers via meta tags
        const meta = document.createElement('meta');
        meta.httpEquiv = 'Cache-Control';
        meta.content = 'no-cache, no-store, must-revalidate';
        document.head.appendChild(meta);

        const pragma = document.createElement('meta');
        pragma.httpEquiv = 'Pragma';
        pragma.content = 'no-cache';
        document.head.appendChild(pragma);

        const expires = document.createElement('meta');
        expires.httpEquiv = 'Expires';
        expires.content = '0';
        document.head.appendChild(expires);

        // Disable browser back/forward cache (bfcache)
        window.history.scrollRestoration = 'manual';

        // Handle page show event (fires when page is loaded from cache)
        const handlePageShow = (event) => {
            if (event.persisted || (window.performance && window.performance.navigation.type === 2)) {
                // Page was loaded from cache, check authentication
                if (!isAuthenticated()) {
                    window.location.href = '/login';
                }
            }
        };

        window.addEventListener('pageshow', handlePageShow);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
            document.head.removeChild(meta);
            document.head.removeChild(pragma);
            document.head.removeChild(expires);
        };
    }, []);

    // Check authentication on every render
    if (!isAuthenticated()) {
        return <Navigate to="/login" replace />;
    }

    return children;
}

export default ProtectedRoute;
